import { Helmet } from "react-helmet-async";

interface MedicalCondition {
  name: string;
  code?: string;
  codingSystem?: string;
}

interface MedicalWebPageSchemaProps {
  name: string;
  description: string;
  url: string;
  medicalAudience?: string[];
  about?: MedicalCondition;
  mainEntity?: object;
  lastReviewed?: string;
  specialty?: string;
}

/**
 * MedicalWebPage schema for treatment and medical content pages.
 * Provides enhanced structured data for medical content with audience targeting.
 * 
 * @see https://schema.org/MedicalWebPage
 */
export function MedicalWebPageSchema({
  name,
  description,
  url,
  medicalAudience = ["Patient"],
  about,
  mainEntity,
  lastReviewed,
  specialty,
}: MedicalWebPageSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name,
    description,
    url,
    ...(medicalAudience.length > 0 && {
      audience: {
        "@type": "MedicalAudience",
        audienceType: medicalAudience.join(", "),
      },
    }),
    ...(about && {
      about: {
        "@type": "MedicalCondition",
        name: about.name,
        ...(about.code && { code: about.code }),
        ...(about.codingSystem && {
          codingSystem: about.codingSystem,
        }),
      },
    }),
    ...(mainEntity && { mainEntity }),
    ...(lastReviewed && { lastReviewed }),
    ...(specialty && {
      specialty: {
        "@type": "MedicalSpecialty",
        name: specialty,
      },
    }),
    inLanguage: "en-US",
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
